import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, User, FileText, LogOut, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/stores/auth';

function Ic({ type, size = 20, color = 'var(--soleil-gold)' }: { type: string; size?: number; color?: string }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: '2', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const icons: Record<string, React.ReactNode> = {
    sun: <svg {...p}><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>,
  };
  return <>{icons[type] || null}</>;
}

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

export default function SoleilDashboardLayout() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { href: '/dashboard', icon: LayoutDashboard, label: t('dashboard.nav.dashboard') },
    { href: '/dashboard/profile', icon: User, label: t('dashboard.nav.profile') },
    { href: '/dashboard/invoices', icon: FileText, label: i18n.language === 'ar' ? 'فواتيري' : 'My Invoices' },
    { href: '/dashboard/exam-orders', icon: FileText, label: isRTL ? 'امتحاناتي' : 'My Exams' },
  ];

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const isActive = (href: string) => location.pathname === href;

  return (
    <div className="soleil" style={{ minHeight: '100vh', fontFamily: S.font }}>
      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'var(--soleil-dark, #3C3632)', backdropFilter: 'none',
        borderBottom: '1px solid rgba(255,255,255,0.1)', height: 64,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: 4 }}
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Ic type="sun" size={16} color="white" />
              </div>
              <span style={{ fontFamily: S.heading, fontSize: 17, fontWeight: 600, color: '#fff' }}>ITIL Shift</span>
            </Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span className="hidden md:block" style={{ fontFamily: S.font, fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>{user?.name}</span>
            <button
              onClick={handleLogout}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', padding: 6, borderRadius: 8 }}
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <div style={{ display: 'flex' }}>
        {/* Sidebar */}
        <aside
          className={`${sidebarOpen ? 'block' : 'hidden'} md:block`}
          style={{
            width: 256, minHeight: 'calc(100vh - 64px)',
            background: 'var(--soleil-cream)', borderRight: '1px solid var(--soleil-sand)',
            padding: 16,
          }}
        >
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px', borderRadius: 10, textDecoration: 'none',
                  fontFamily: S.font, fontSize: 14, fontWeight: isActive(item.href) ? 600 : 500,
                  background: isActive(item.href) ? 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))' : 'transparent',
                  color: isActive(item.href) ? 'var(--soleil-dark)' : 'var(--soleil-sub)',
                  transition: 'all 0.15s ease',
                }}
              >
                <item.icon size={18} />
                {item.label}
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main style={{ flex: 1, padding: 24, background: 'var(--soleil-cream)', minHeight: 'calc(100vh - 64px)' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
